import axios from 'axios';
import { Storage } from "@/services/Storage";
import store from '@/store';

// Base URL: window.lytstoreApi (set in GlobalVariables.js)
// No authentication required per spec

const base = () => {
  if (!window.lytstoreApi) {
    throw new Error('lytstoreApi is not configured on window');
  }
  return window.lytstoreApi;
};

// Helpers to build urls
const url = {
  pending: (storeDB) => `${base()}/${encodeURIComponent(storeDB)}/orders/pending`,
  list: (storeDB) => `${base()}/${encodeURIComponent(storeDB)}/orders`,
  get: (storeDB, orderId) => `${base()}/${encodeURIComponent(storeDB)}/orders/${encodeURIComponent(orderId)}`,
  getSale: (storeDB, saleNum) => `${base()}/${encodeURIComponent(storeDB)}/orders/sale/${encodeURIComponent(saleNum)}`,
  accept: (storeDB, orderId) => `${base()}/${encodeURIComponent(storeDB)}/orders/${encodeURIComponent(orderId)}/accept`,
  reject: (storeDB, orderId) => `${base()}/${encodeURIComponent(storeDB)}/orders/${encodeURIComponent(orderId)}/reject`,
  adjust: (storeDB, orderId) => `${base()}/${encodeURIComponent(storeDB)}/orders/${encodeURIComponent(orderId)}/items/adjust`,
  updateStatus: (storeDB, orderId) => `${base()}/${encodeURIComponent(storeDB)}/orders/${encodeURIComponent(orderId)}/status`,
  registration: (storeDB) => `${base()}/${encodeURIComponent(storeDB)}/lytconnect/registration`,
};

const api = {
  async getPending(storeDB) {
    const { data } = await axios.get(url.pending(storeDB));
    return data;
  },
  async getOrders(storeDB, params = {}) {
    const { data } = await axios.get(url.list(storeDB), { params });
    return data;
  },
  async getOrder(storeDB, orderId) {
    const { data } = await axios.get(url.get(storeDB, orderId));
    return data;
  },
  async getSale(storeDB, saleNum) {
    const { data } = await axios.get(url.getSale(storeDB, saleNum));
    return data;
  },
  async checkRegistration(storeDB) {
    const { data } = await axios.get(url.registration(storeDB));
    return data;
  },
  async acceptOrder(storeDB, orderId, saleNum, reason) {
    const resolvedSaleNumber = saleNum ?? store.getters.getCurrentSale ?? 0;

    const payload = {
      saleNumber: String(resolvedSaleNumber),
      reason: reason
    };
    const { data } = await axios.post(url.accept(storeDB, orderId), payload);
    return data;
  },
  async rejectOrder(storeDB, orderId, reason) {
    const payload = { reason };
    const { data } = await axios.post(url.reject(storeDB, orderId), payload);
    return data;
  },
  async adjustItems(storeDB, orderId, payload) {
    // payload per spec: { items: [{ itemId, action, newQuantity, itemReason }], reason }
    const { data } = await axios.post(url.adjust(storeDB, orderId), payload);
    return data;
  },
  async updateOrderStatus(storeDB, orderId, status, notes, saleNumber, invoiceNumber) {
    const payload = { status, notes };
    if (saleNumber !== undefined && saleNumber !== null && String(saleNumber).trim() !== '') {
      payload.saleNumber = String(saleNumber).trim();
    }
    if (invoiceNumber !== undefined && invoiceNumber !== null && String(invoiceNumber).trim() !== '') {
      payload.invoiceNumber = String(invoiceNumber).trim();
    }
    const { data } = await axios.put(url.updateStatus(storeDB, orderId), payload);
    return data;
  },

  async acceptLytStoreOrderAndCreateTable(storeDB, order) {
    // The 'order' parameter is the order data object itself.
    // No need to access a .data property.

    // 1. Get current user and device settings
    const user = store.getters.getCurrentUser;
    if (!user || !user.Username) {
      throw new Error('Could not determine current logged-in user.');
    }
    const settings = await Storage.getDeviceSettings();

    // 2. Transform LytStore order items into the sale format
    const saleItems = order.items.map((item, index) => {
      const qty = Number(item.quantity);
      const unit = Number(item.unitPrice);
      // Assuming no item-level discount is provided in the LytStore order item
      const discount = 0;
      const lineTotal = (qty * unit) - discount;

      let instructions = [];
      if (item.specialInstructions) {
        try {
          // Safely parse the JSON string for special instructions
          instructions = JSON.parse(item.specialInstructions);
          if (!Array.isArray(instructions)) {
            instructions = [];
          }
        } catch (e) {
          console.error('Failed to parse specialInstructions JSON:', e);
          instructions = [];
        }
      }

      return {
        Plu: item.productPlu,
        Description: item.productName,
        Qty: qty,
        Unit: unit,
        Discount: discount,
        Line: lineTotal,
        instructions: instructions,
        saleLine: index + 1,
      };
    });

    // 3. Construct the payload for the 'tables' endpoint
    const createTablePayload = {
      SaleNum: 0, // 0 indicates a new table/sale
      Name: `shop.co.zm Order #${order.orderNumber}`,
      User: user.Username,
      PC: settings.PC,
      items: saleItems,
      Customer: order.payment.debtorId, // Using debtorId from the payment object
      discount: order.pricing.subtotal - order.pricing.totalAmount, // Calculate sale-level discount
    };

    // 4. First API Call: Create the sale/table in the POS backend
    const createTableResponse = await axios.post(
      window.serverApi + `tables/${storeDB}`,
      createTablePayload
    );

    // 5. Extract the SaleNum and Name from the response
    const saleNum = createTableResponse.data.SaleNum;
    const tableName = createTableResponse.data.Name;

    if (!saleNum) {
      throw new Error('Failed to create table or retrieve SaleNum from the backend.');
    }

    // 6. Second API Call: Accept the order in LytStore, providing the new SaleNum and Name as reason
    return api.acceptOrder(storeDB, order.id, saleNum, tableName);
  }
};

export default api;
